import { useState, useCallback, useEffect, useRef } from 'react';

// Global image cache
const imageCache = new Map();
const loadedImages = new Set();
const loadingImages = new Set();

// Preload images in batches with priority
export const preloadImages = (urls, batchSize = 5, priority = 'normal') => {
  const batches = [];
  for (let i = 0; i < urls.length; i += batchSize) {
    batches.push(urls.slice(i, i + batchSize));
  }
  
  batches.forEach((batch, index) => {
    const delay = priority === 'high' ? index * 50 : index * 100;
    
    setTimeout(() => {
      batch.forEach(url => {
        if (!loadedImages.has(url) && !loadingImages.has(url)) {
          loadingImages.add(url);
          
          const img = new Image();
          img.onload = () => {
            loadedImages.add(url);
            imageCache.set(url, img);
            loadingImages.delete(url);
          };
          img.onerror = () => {
            loadingImages.delete(url);
          };
          img.src = url;
        }
      });
    }, delay);
  });
};

// Hook for image optimization
export const useImageOptimization = (games = [], options = {}) => {
  const {
    preloadCount = 15,
    batchSize = 4,
    priority = 'normal',
    enableLazyLoading = true
  } = options;

  const [loadedImages, setLoadedImages] = useState(new Set());
  const [imageErrors, setImageErrors] = useState(new Set());
  const [isPreloading, setIsPreloading] = useState(false);
  const observerRef = useRef(null);

  // Preload images when games change
  useEffect(() => {
    if (games && games.length > 0) {
      setIsPreloading(true);
      const imageUrls = games.slice(0, preloadCount).map(game => game.game_icon);
      
      preloadImages(imageUrls, batchSize, priority);
      
      // Reset preloading state after a delay
      setTimeout(() => {
        setIsPreloading(false);
      }, 1000);
    }
  }, [games, preloadCount, batchSize, priority]);

  const handleImageLoad = useCallback((gameId) => {
    setLoadedImages(prev => new Set([...prev, gameId]));
  }, []);

  const handleImageError = useCallback((gameId) => {
    setImageErrors(prev => new Set([...prev, gameId]));
  }, []);

  const isImageLoaded = useCallback((gameId) => {
    return loadedImages.has(gameId);
  }, [loadedImages]);

  const isImageError = useCallback((gameId) => {
    return imageErrors.has(gameId);
  }, [imageErrors]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    loadedImages,
    imageErrors,
    isPreloading,
    handleImageLoad,
    handleImageError,
    isImageLoaded,
    isImageError,
    preloadImages: (urls) => preloadImages(urls, batchSize, priority)
  };
};

// Hook for virtual scrolling optimization
export const useVirtualScrolling = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });

  useEffect(() => {
    const visibleCount = Math.ceil(containerHeight / itemHeight) + 2; // Add buffer
    const start = Math.floor(scrollTop / itemHeight);
    const end = Math.min(start + visibleCount, items.length);

    setVisibleRange({ start, end });
  }, [scrollTop, itemHeight, containerHeight, items.length]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const visibleItems = items.slice(visibleRange.start, visibleRange.end);
  const totalHeight = items.length * itemHeight;
  const offsetY = visibleRange.start * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll
  };
};

export default useImageOptimization;
